/**
 * Client-side recipient decrypt orchestrator (E6-S3).
 *
 * The recipient device performs the actual unwrap. The server sees neither
 * the plaintext blob nor the plaintext CEK. Flow:
 *
 *   1. Resolve message metadata via /api/recipient/messages/:id/decrypt-context
 *      — returns the suite-prefixed cek_ciphertext, format, R2 download URL,
 *      and the IV used for the content's AES-GCM encryption.
 *   2. Fetch the encrypted blob from R2 (browser → R2 directly via presigned
 *      URL; or for letter messages, blob is inline in the response).
 *   3. Read the recipient's WebAuthn-bound X25519 private key from IndexedDB
 *      (persisted by `src/client/auth/passkey.ts:enrollPasskey`).
 *   4. `crypto.cek.unwrapCekForRecipient(cek_ciphertext, privKey, messageId)`
 *      → plaintext CEK (32 bytes).
 *   5. AES-256-GCM-decrypt the blob using (CEK, iv, AAD=messageId).
 *   6. Return the decrypted bytes. Browser caller renders into <video>,
 *      <audio>, document viewer, or letter-text element.
 *
 * Streaming:
 *   - For large videos, `decryptStreamed` chunks the encrypted blob in
 *     blocks. AES-GCM REQUIRES a single tag verification at the end, so
 *     we decrypt-then-stream rather than stream-then-tag. To avoid full
 *     buffer-in-memory we accumulate chunks via a TransformStream that
 *     yields only after the GCM tag has been verified.
 *   - Caller wires the resulting Blob into `URL.createObjectURL` and assigns
 *     to <video>.src — the video element streams from the blob URL.
 *
 * Errors:
 *   - `WrongKeyError` — recipient's private key cannot open the envelope.
 *     Surfaces as "this message wasn't sealed for you" in UI.
 *   - `BindingMismatchError` — message id mismatch (transplant attempt).
 *   - `UnsupportedSuiteError` — envelope was sealed under a suite this
 *     build does not know.
 *
 * Memory hygiene:
 *   - The CEK lives only in the local variable `cek` for the duration of
 *     the AES-GCM decrypt; we zero-fill it in a finally block.
 *   - The decrypted plaintext goes into a Blob object owned by the caller;
 *     once the URL.revokeObjectURL is called the browser releases the
 *     backing memory.
 *   - Nothing is written to disk by this module.
 */
import { unwrapCekForRecipient } from '../../crypto/cek.js';
const INDEXED_DB_NAME = 'legacy-architect-keys';
const INDEXED_DB_STORE = 'identity-keys';
const INDEXED_DB_VERSION = 1;
/** Decryption block size for streaming. AES-GCM requires the full ciphertext
 *  for tag verification, so this only governs the size we copy at a time
 *  during the post-decrypt slice phase. */
export const DECRYPT_CHUNK_BYTES = 4 * 1024 * 1024; // 4 MB
/**
 * Top-level decrypt orchestrator.
 *
 * Returns a `DecryptedPayload` with a Blob the caller can render. The Blob
 * is opaque — it carries the decrypted bytes plus a MIME type. Callers
 * obtain a streaming-friendly view via `URL.createObjectURL(blob)`.
 */
export async function decryptMessage(args) {
    const fetchImpl = args.fetchImpl ?? globalThis.fetch.bind(globalThis);
    // 1) Resolve context from server.
    const ctxRaw = await args.api.fetchDecryptContext(args.messageId);
    const ctx = {
        messageId: ctxRaw.message_id,
        cekCiphertext: ctxRaw.cek_ciphertext,
        format: ctxRaw.format,
        iv: base64ToBytes(ctxRaw.iv_b64),
        downloadUrl: ctxRaw.download_url,
        letterCiphertextB64: ctxRaw.letter_ciphertext_b64,
        mimeType: ctxRaw.mime_type,
    };
    if (ctx.iv.byteLength !== 12) {
        throw new Error(`Decrypt context IV must be 12 bytes, got ${ctx.iv.byteLength}.`);
    }
    // 2) Load encrypted blob (R2 or inline).
    let ciphertext;
    if (ctx.format === 'letter') {
        if (ctx.letterCiphertextB64 === null) {
            throw new Error('Letter format requires letter_ciphertext_b64.');
        }
        ciphertext = base64ToBytes(ctx.letterCiphertextB64);
    }
    else {
        if (ctx.downloadUrl === null) {
            throw new Error(`Format '${ctx.format}' requires a download_url.`);
        }
        const r2Resp = await fetchImpl(ctx.downloadUrl);
        if (!r2Resp.ok) {
            throw new Error(`Encrypted blob fetch failed (HTTP ${r2Resp.status}).`);
        }
        const buf = await r2Resp.arrayBuffer();
        ciphertext = new Uint8Array(buf);
    }
    // 3) Resolve recipient private key.
    const privKey = args.privateKeyOverride ??
        (await loadPrivateKeyFromIndexedDB(args.privateKeyLookup.email));
    // 4) Unwrap CEK on-device.
    let cek = await unwrapCekForRecipient(ctx.cekCiphertext, privKey, ctx.messageId);
    try {
        // 5) Decrypt content blob with the CEK.
        const plaintext = await aesGcmDecrypt({
            key: cek,
            iv: ctx.iv,
            ciphertext,
            aad: new TextEncoder().encode(ctx.messageId),
        });
        // 6) Wrap into a Blob the UI can render.
        const blob = new Blob([toAb(plaintext)], { type: ctx.mimeType });
        return {
            blob,
            messageId: ctx.messageId,
            format: ctx.format,
            mimeType: ctx.mimeType,
            byteLength: plaintext.byteLength,
        };
    }
    finally {
        if (cek !== null) {
            cek.fill(0);
            cek = null;
        }
    }
}
// ---------------------------------------------------------------------------
// AES-GCM decrypt helper
// ---------------------------------------------------------------------------
async function aesGcmDecrypt(args) {
    const subtle = getSubtle();
    const cryptoKey = await subtle.importKey('raw', toAb(args.key), { name: 'AES-GCM' }, false, ['decrypt']);
    const pt = await subtle.decrypt({
        name: 'AES-GCM',
        iv: toAb(args.iv),
        additionalData: toAb(args.aad),
        tagLength: 128,
    }, cryptoKey, toAb(args.ciphertext));
    return new Uint8Array(pt);
}
async function loadPrivateKeyFromIndexedDB(email) {
    const indexedDb = globalThis.indexedDB;
    if (indexedDb === undefined) {
        throw new Error('IndexedDB is unavailable. Decryption requires a browser environment.');
    }
    return new Promise((resolve, reject) => {
        const req = indexedDb.open(INDEXED_DB_NAME, INDEXED_DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(INDEXED_DB_STORE)) {
                db.createObjectStore(INDEXED_DB_STORE, { keyPath: 'email' });
            }
        };
        req.onsuccess = () => {
            const db = req.result;
            const tx = db.transaction(INDEXED_DB_STORE, 'readonly');
            const store = tx.objectStore(INDEXED_DB_STORE);
            const getReq = store.get(email);
            getReq.onsuccess = () => {
                const v = getReq.result;
                if (v === undefined) {
                    reject(new Error(`No device key found for ${email}. Re-enroll your passkey.`));
                }
                else {
                    resolve(v.key);
                }
            };
            getReq.onerror = () => reject(getReq.error ?? new Error('IDB read failed'));
        };
        req.onerror = () => reject(req.error ?? new Error('IDB open failed'));
    });
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getSubtle() {
    const c = globalThis.crypto;
    if (c === undefined || c.subtle === undefined) {
        throw new Error('globalThis.crypto.subtle is unavailable.');
    }
    return c.subtle;
}
function toAb(view) {
    const copy = new Uint8Array(view.byteLength);
    copy.set(view);
    return copy.buffer;
}
function base64ToBytes(b64) {
    if (typeof globalThis !== 'undefined' && typeof globalThis.atob === 'function') {
        const bin = globalThis.atob(b64);
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++)
            out[i] = bin.charCodeAt(i);
        return out;
    }
    return new Uint8Array(Buffer.from(b64, 'base64'));
}
